import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { BreadcrumbJsonLd, ItemListJsonLd } from "../components/JsonLd";
import { formatReadingTime } from "../lib/readingTime";
import { ArrowRight, ArrowLeft, Calendar, Flame, Eye, Clock, Trophy } from "lucide-react";

const PAGE_SIZE = 6;

export default function Blog() {
  const [articles, setArticles] = useState([]);
  const [paging, setPaging] = useState({ tag: null, page: 1 });
  const [activeTag, setActiveTag] = useState(null);
  const page = paging.tag === activeTag ? paging.page : 1;
  const setPage = (next) => setPaging(prev => {
    const base = prev.tag === activeTag ? prev.page : 1;
    return { tag: activeTag, page: typeof next === "function" ? next(base) : next };
  });

  useEffect(() => {
    api.get("/articles").then(r => setArticles(r.data));
  }, []);

  const allTags = useMemo(() => {
    const set = new Set();
    articles.forEach(a => (a.tags || []).forEach(t => set.add(t)));
    return Array.from(set).sort();
  }, [articles]);

  const filtered = useMemo(() => {
    if (!activeTag) return articles;
    return articles.filter(a => (a.tags || []).includes(activeTag));
  }, [articles, activeTag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const mostReadId = useMemo(() => {
    if (articles.length === 0) return null;
    const top = articles.reduce((max, a) => (a.views || 0) > (max.views || 0) ? a : max, articles[0]);
    return (top.views || 0) > 0 ? top.id : null;
  }, [articles]);

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-12">
      <BreadcrumbJsonLd items={[{ name: "Accueil", path: "/" }, { name: "Blog", path: "/blog" }]} />
      <ItemListJsonLd
        name="Blog Fedevo 2.0 · Culture urbaine et streaming"
        description="Analyses, portraits et reportages sur la culture hip-hop, le graffiti, le breakdance et le streaming urbain."
        items={articles.map(a => ({ name: a.title, path: `/blog/${a.slug}`, image: a.cover_image, description: a.excerpt }))}
      />
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-[color:var(--accent)] mb-3">Journal</div>
        <div className="flex items-start flex-wrap gap-6">
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl">Le Blog Fedevo 2.0</h1>
          <Link
            to="/blog/top"
            data-testid="link-blog-top"
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs uppercase tracking-widest font-semibold shadow-lg hover:brightness-110 transition"
          >
            <Trophy className="w-4 h-4" /> Top 10 · Le + lu
          </Link>
        </div>
        <p className="text-[color:var(--text-muted)] mt-4 max-w-2xl">
          Analyses, portraits et reportages sur la street culture — la mémoire écrite de notre diffusion.
        </p>
      </div>

      {allTags.length > 0 && (
        <div className="mb-8 flex items-center gap-2 flex-wrap" data-testid="blog-tag-filter">
          <button
            onClick={() => setActiveTag(null)}
            data-testid="tag-all"
            className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.25em] border transition-colors ${
              !activeTag
                ? "bg-[color:var(--accent)] text-black border-[color:var(--accent)] font-semibold"
                : "border-[color:var(--border)] text-[color:var(--text-muted)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
            }`}
          >
            Tous ({articles.length})
          </button>
          {allTags.map(t => (
            <button
              key={t}
              onClick={() => setActiveTag(t)}
              data-testid={`tag-${t}`}
              className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.25em] border transition-colors normal-case ${
                activeTag === t
                  ? "bg-[color:var(--accent)] text-black border-[color:var(--accent)] font-semibold"
                  : "border-[color:var(--border)] text-[color:var(--text-muted)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 stagger" data-testid="blog-grid">
        {paged.map(a => (
          <Link
            to={`/blog/${a.slug}`}
            key={a.id}
            data-testid={`article-card-${a.slug}`}
            className="card-hover rounded-2xl overflow-hidden border border-[color:var(--border)] bg-[color:var(--bg-elev)] block relative"
          >
            {a.cover_image && (
              <div className="aspect-[16/9] bg-black overflow-hidden relative">
                <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover" />
                {a.id === mostReadId && (
                  <div
                    data-testid={`most-read-badge-${a.slug}`}
                    className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-orange-500 to-red-500 text-white text-[10px] uppercase tracking-widest font-bold rounded shadow-lg"
                  >
                    <Flame className="w-3 h-3" /> Le + lu
                  </div>
                )}
              </div>
            )}
            <div className="p-6">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[color:var(--text-muted)] mb-3 flex-wrap">
                <Calendar className="w-3 h-3" />
                {new Date(a.published_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {formatReadingTime(a.content)}
                </span>
                {a.tags?.slice(0, 2).map(t => (
                  <span key={t} className="px-2 py-0.5 rounded-full bg-[color:var(--bg)] border border-[color:var(--border)] normal-case">{t}</span>
                ))}
                {typeof a.views === "number" && (
                  <span className="inline-flex items-center gap-1 ml-auto">
                    <Eye className="w-3 h-3" /> {a.views}
                  </span>
                )}
              </div>
              <h2 className="font-display text-3xl leading-tight mb-2">{a.title}</h2>
              <p className="text-sm text-[color:var(--text-muted)] line-clamp-3">{a.excerpt}</p>
              <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[color:var(--accent)]">
                Lire l'article <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-20 text-[color:var(--text-muted)]">
          {activeTag ? `Aucun article dans la catégorie "${activeTag}".` : "Aucun article pour le moment."}
        </div>
      )}

      {totalPages > 1 && (
        <nav
          data-testid="blog-pagination"
          aria-label="Pagination des articles"
          className="mt-12 flex items-center justify-center gap-2 flex-wrap"
        >
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            data-testid="page-prev"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[color:var(--border)] text-xs uppercase tracking-widest hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] disabled:opacity-40 disabled:hover:border-[color:var(--border)] disabled:hover:text-inherit transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Précédent
          </button>
          <div className="flex items-center gap-1" data-testid="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
              <button
                key={n}
                onClick={() => setPage(n)}
                data-testid={`page-${n}`}
                aria-current={n === currentPage ? "page" : undefined}
                className={`w-9 h-9 rounded-full text-sm font-mono transition-colors ${
                  n === currentPage
                    ? "bg-[color:var(--accent)] text-black font-semibold"
                    : "border border-[color:var(--border)] text-[color:var(--text-muted)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)]"
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            data-testid="page-next"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[color:var(--border)] text-xs uppercase tracking-widest hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] disabled:opacity-40 disabled:hover:border-[color:var(--border)] disabled:hover:text-inherit transition-colors"
          >
            Suivant <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </nav>
      )}
    </div>
  );
}
